import { useContext, useEffect, useState } from 'react';

import { Typography } from 'antd';

import {
  ColSize,
  ConfirmationModal,
  Form,
  RowSize,
} from '../../../../../../../../../shared/components';
import { Modal } from '@hdi-seguros/web-components.modal';
import {
  ModalDeleteTab,
  PlanTypeSection,
  QuotationStepButtons,
} from '../../../../../shared';
import { Tabs } from '@hdi-seguros/web-components.tabs';

import QuotationService from '../../../../../../../../../shared/services/quotation';

import {
  GenericDataFormContext,
  QuotationInfoContext,
} from '../../../../../shared/contexts';
import { StepThreeFormContext } from '../../contexts/StepThreeForm';
import { WizardDataFormContext } from '../../../../Wizard/contexts/WizardDataForm';

import { DECISION_TYPE, FeatureRisk } from '../../../../../../../../../shared/interfaces';
import { QuotationStep } from '../../../../../shared/interfaces';

import { validateCodeFeatures } from '../../../../../utils';

export const PropertyDataCommerce = () => {
  const {
    activeKey,
    addNewTab,
    allFeatures,
    keyTabToDelete,
    removeTab,
    setActiveKey,
    setKeyTabToDelete,
    setShowModal,
    showModal,
    stepThreeForm,
    tabs,
    hasClose,
    hasOpen,
    setPlansByActivityList,
    plansByActivityListCopy,
    resetTabs,
    commerceFormValues, 
    setCommerceFormValues
  } = useContext(StepThreeFormContext);
  const { quotationForm, handleTabErrors } = useContext(WizardDataFormContext);
  const { setCurrentStep, quotationInfo, savedQuotation } = useContext(QuotationInfoContext);
  const { setCanShowSpinner, setUserCanDeleteTabs } = useContext(GenericDataFormContext);

  const [canShowConfirmationModal, setCanShowConfirmationModal] = useState<boolean>(false);
  const [planType, setPlanType] = useState<string | undefined>(DECISION_TYPE.NO);
  const [planTypeChange, setPlanTypeChange] = useState<string>();
  const [viewModalError, setViewModalError] = useState<boolean>(false);

  const validateFeatures = () => {
    return new Promise(async (resolve, reject) => {
      let hasError = false;

      try {
        for (const { key } of tabs) {
          const featureList: FeatureRisk[] = [];
          const featuresFiltered = allFeatures
            .filter((feature) => String(feature.planCode) === String(stepThreeForm?.getFieldValue(`plan-tab-${key}`)))
            .map(({ features }) => features);

          const allFieldsValues = stepThreeForm?.getFieldsValue();
          const hasDuplicateFeatures = validateCodeFeatures(
            allFieldsValues,
            `feature-tab-${key}`,
            true,
            4
          );

          if (hasDuplicateFeatures) {
            setViewModalError(true);
            throw new Error();
          }

          featuresFiltered.forEach((featureGroup) => {
            featureGroup.forEach(({ codigo, codigoEquivalente }) => {
              const fieldName = `feature-tab-${key}-${codigo}-${codigoEquivalente}`;
              const fieldValue = stepThreeForm?.getFieldValue(fieldName);
              const alreadyExists = featureList.some(
                (feature) => feature.codigo === codigo
              );
              if (!alreadyExists) {
                featureList.push({
                  codigo,
                  valor: fieldValue,
                });
              }
            });
          });

          const params = {
            numeroCotizacion: Number(quotationInfo?.token),
            rama: Number(quotationInfo?.branch),
            componente: key,
            codigoPostal: Number(quotationForm?.cliente?.codigoPostal),
            sufijoPostal: Number(quotationForm?.cliente?.sufijoCodigoPostal),
            caracteristicas: featureList,
          };

          if (!hasError) {
            try {
              await QuotationService.postValidateFeaturesRiskVarious(params);
            } catch (error) {
              hasError = true;
              reject(
                new Error(`An error occurred during validation in tab ${key}`)
              );
              return;
            }
          }
        }

        resolve('');
      } catch (error) {
        reject(new Error('Validation failed'));
      }
    });
  };

  const onValidateForm = async () => {
    try {
      setCanShowSpinner(true);
      await stepThreeForm?.validateFields();

      await validateFeatures();
      setCommerceFormValues(stepThreeForm?.getFieldsValue());
      setUserCanDeleteTabs(false);
      setCurrentStep(QuotationStep.FOUR);
    } catch (error: any) {
      if (error && error?.errorFields?.length > 0) {
        handleTabErrors(error?.errorFields, ['tab'], 'Integral de Comercio');
      }
    } finally {
      setCanShowSpinner(false);
    }
  };

  const onChange = (newActiveKey: string) => {
    setActiveKey(newActiveKey);
  };

  const onEdit = (targetKey: any, action: 'add' | 'remove') => {
    if (action === 'add') {
      addNewTab();
      return;
    }

    setShowModal(true);
  };

  const handlerBackButton = () => {
    setCommerceFormValues(stepThreeForm?.getFieldsValue());
    setCurrentStep(QuotationStep.TWO);
  };

  const handleOk = () => {
    if (keyTabToDelete !== null) {
      removeTab(keyTabToDelete);
      setKeyTabToDelete(null);
    }

    setShowModal(false);
  };

  const handleCancel = () => {
    setShowModal(false);

    setKeyTabToDelete(null);
  };

  const handleChangePlanType = (event: any) => {
    setPlanTypeChange(event.target.value);
    setCanShowConfirmationModal(true);
  };

  const handleActionModal = (isConfirm: boolean) => {
    if (isConfirm) {
      const newListPlan = plansByActivityListCopy.data.planes?.filter(
        (plan: any) => plan.tipoDePlan === planTypeChange
      );

      const options = newListPlan.map((plan: any) => ({
        label: plan.descripcionLarga,
        value: plan.codigo,
      }));

      setPlansByActivityList(options);
      setCanShowConfirmationModal(false);

      resetTabs();
      stepThreeForm?.setFieldValue('planType', planTypeChange);

      return;
    }

    stepThreeForm?.setFieldValue(
      'planType',
      planTypeChange === 'C' ? 'A' : 'C'
    );
    setCanShowConfirmationModal(false);
  };

  useEffect(() => {
    if (hasClose && !hasOpen) {
      setPlanType('C');
      stepThreeForm?.setFieldValue('planType', 'C');
    }

    if (!hasClose && hasOpen) {
      setPlanType('A');
      stepThreeForm?.setFieldValue('planType', 'A');
    }
  }, [planType, hasClose, hasOpen]);

  useEffect(() => {
    if (
      commerceFormValues &&
      Object.keys(commerceFormValues).length > 0 &&
      !savedQuotation
    ) {
      stepThreeForm?.setFieldsValue(commerceFormValues);
    }
  }, [commerceFormValues, stepThreeForm]);

  return (
    <>
      <Form
        formProps={{
          form: stepThreeForm,
          name: 'property-data-commerce-form',
        }}
      >
        <PlanTypeSection
          nameFormItem='planType'
          planType={planType}
          onChange={(event) => handleChangePlanType(event)}
        />

        <Tabs
          type='editable-card'
          activeKey={activeKey}
          onEdit={onEdit}
          hideAdd={stepThreeForm?.getFieldValue('planType') === 'C'}
          items={tabs}
          handlePressTab={onChange}
        />
      </Form>

      <QuotationStepButtons
        label='Continuar'
        onClick={onValidateForm}
        onPressBackButton={handlerBackButton}
      />

      <ModalDeleteTab
        open={showModal}
        onCancel={handleCancel}
        onOk={handleOk}
      />

      <Modal
        title='Error al continuar.'
        open={viewModalError}
        centered
        width={650}
        onCancel={() => setViewModalError(false)}
        confirmText='Aceptar'
      >
        <RowSize
          rowProps={{ align: 'middle', justify: 'end' }}
          className='mb-1'
        >
          <ColSize size={100}>
            <Typography.Text>
              Solo se puede seleccionar una característica por código.
            </Typography.Text>
          </ColSize>
        </RowSize>
      </Modal>

      <ConfirmationModal
        open={canShowConfirmationModal}
        onCancel={() => handleActionModal(false)}
        onConfirm={() => handleActionModal(true)}
        confirmButtonLabel='Confirmar'
        title='¿Desea cambiar el Tipo de Plan?'
        description='Si cambia el Tipo de Plan, los datos cargados de esta operación serán eliminados y tendrá que volver a cargar de vuelta todos los datos de la misma. ¿Desea continuar?'
      />
    </>
  );
};
